<template>
    <div>
        <h3 class="grey-color main_text font-weight-bold">Отчеты по закупкам</h3>

        <text-row v-if="currentBudgetaryManager" :value="currentBudgetaryManager" label="ГРБС" />
        <text-row v-if="currentMunicipality" :value="currentMunicipality" label="Муниципалитет" />

        <div class="table-responsive py-3">
            <b-pagination v-if="reports && meta.total > 0 && meta.last_page > 1" v-model="meta.current_page" :per-page="meta.per_page" :total-rows="meta.total" align="center" class="mb-3" />

            <b-table :busy="loading" :fields="fields" :items="reports" :per-page="meta.per_page" class="table-atmo" empty-text="Данные отсутствуют" show-empty>
                <template #table-busy>
                    <div class="text-center my-2">
                        <b-spinner class="align-middle mr-1"></b-spinner>
                        <strong>Загрузка данных...</strong>
                    </div>
                </template>
                <template #cell(period)="row">
                    {{ `${row.item.report_period_title} ${row.item.year}` }}
                    <template v-if="row.item.status.id !== 'new'">
                        <action-button-small :icon="!row.detailsShowing ? 'eye' : 'eye-slash'" :title="(!row.detailsShowing ? 'Показать' : 'Скрыть') + ' подробности'" @click="row.toggleDetails" />
                    </template>
                </template>
                <template #cell(sent_at)="row">
                    {{ row.item.sent_at ? getDateTimeFormat(row.item.sent_at) : '&mdash;' }}
                </template>
                <template #cell(sent_by_user)="row">
                    <template v-if="row.item.sent_by_user">{{ row.item.sent_by_user }}</template>
                    <template v-else>&mdash;</template>
                </template>
                <template #cell(status)="row">
                    <template v-if="row.item.can_be_filled">
                        <cabinet-fl44-purchase-report-fill-button :fl44-purchase-report="row.item" @refresh="getReports()" />
                    </template>
                    <b-badge v-else :variant="statusBadgeVariant(row.item.status.id)" class="fs-14" pill>{{ row.item.status.title }}</b-badge>
                </template>
                <template #row-details="row">
                    <template v-if="row.item.status.id === 'rejected'">
                        <p v-if="row.item.replied_by">
                            <b>ФИО:</b> {{ row.item.replied_by.name }}<br />
                            <b>Телефон:</b> {{ $parsePhone(row.item.replied_by.phone) }}<br />
                            <b>Email:</b> {{ row.item.replied_by.email }}<br />
                        </p>
                        <p v-if="row.item.comment"><b>Комментарий:</b> {{ row.item.comment }}</p>
                    </template>
                    <div class="table-responsive">
                        <table class="table table-atmo">
                            <thead>
                                <tr>
                                    <th class="text-nowrap" rowspan="2">Основание закупки</th>
                                    <th colspan="2">Сводная информация</th>
                                    <th colspan="2">Закупки у поставщиков из РБ</th>
                                </tr>
                                <tr>
                                    <th>Кол-во</th>
                                    <th>Сумма</th>
                                    <th>Кол-во</th>
                                    <th>Сумма</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="detailItem in row.item.details" :key="detailItem.id">
                                    <td class="text-nowrap">{{ detailItem.order_type_name }}</td>
                                    <td class="text-nowrap" width="100">{{ detailItem.user_total_count }}</td>
                                    <td class="text-right text-nowrap" width="150">{{ $formatPrice(detailItem.user_total_sum) }}</td>
                                    <td class="text-nowrap" width="100">{{ detailItem.user_local_count }}</td>
                                    <td class="text-right text-nowrap" width="150">{{ $formatPrice(detailItem.user_local_sum) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </template>
            </b-table>

            <b-pagination v-if="reports && meta.total > 0 && meta.last_page > 1" v-model="meta.current_page" :per-page="meta.per_page" :total-rows="meta.total" align="center" class="mb-3" @input="getReports()" />
        </div>
    </div>
</template>

<script>
import { $api } from '@/services/backend/api';
import CabinetFl44PurchaseReportFillButton from '@/components/cabinet/fl44/purchase-reports/cabinet-fl44-purchase-report-fill-button.vue';
import { OrgRoles } from '@/const';

export default {
    name: 'cabinet-fl44-purchase-reports',
    components: {
        CabinetFl44PurchaseReportFillButton
    },
    metaInfo: {
        title: 'Закупки 44ФЗ. Отчеты по закупкам'
    },
    data() {
        return {
            loading: false,
            reports: [],
            meta: {
                current_page: 1,
                total: 0,
                per_page: 15
            },
            fields: [
                { key: 'period', label: 'Период', tdClass: 'text-nowrap' },
                { key: 'version', label: '№ версии' },
                { key: 'sent_at', label: 'Дата и время отправки' },
                { key: 'sent_by_user', label: 'ФИО пользователя' },
                { key: 'status', label: 'Статус отчета' }
            ]
        };
    },
    async mounted() {
        await this.getReports();
    },
    methods: {
        getFilters(page = true) {
            return {
                page: page ? this.meta.current_page : 1
            };
        },
        async getReports(page = true, loading = true) {
            this.loading = loading;
            const response = await $api.personal.fl44.getPurchaseReportsList(this.getFilters(page));
            if (response) {
                ({ data: this.reports, meta: this.meta } = response);
                this.$scrollToTop();
            }
            this.loading = false;
        },
        refresh() {
            this.getReports();
        },
        statusBadgeVariant(status) {
            switch (status) {
                case 'new':
                case 'processing':
                case 'rejected':
                    return 'secondary';
                case 'error':
                    return 'danger';
                case 'accepted':
                    return 'custom-green';
            }
        }
    },
    computed: {
        currentMunicipality() {
            return this.orgRoleIsPermitted(OrgRoles.CUSTOMER_FL44) && this.$store.state.organization.municipality_name;
        },
        currentBudgetaryManager() {
            return this.orgRoleIsPermitted(OrgRoles.CUSTOMER_FL44) && this.$store.state.organization.budgetary_manager_name;
        }
    }
};
</script>
